import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TalentBioData {
  bio: string | null;
  isEdited: boolean;
  generatedBio: string | null;
  isLoading: boolean;
  isGenerating: boolean;
  isSaving: boolean;
}

interface UseTalentBioProps {
  talentType: 'athlete' | 'creator';
  talentId: string;
  platform?: string;
  name: string;
  sport?: string;
  position?: string;
  team?: string;
  league?: string;
  stats?: Record<string, unknown>;
  followerCount?: number;
  videoCount?: number;
  existingBio?: string | null;
}

export const useTalentBio = ({
  talentType,
  talentId,
  platform,
  name,
  sport,
  position,
  team,
  league,
  stats,
  followerCount,
  videoCount,
  existingBio,
}: UseTalentBioProps) => {
  const { toast } = useToast();
  const [data, setData] = useState<TalentBioData>({
    bio: existingBio || null,
    isEdited: false,
    generatedBio: null,
    isLoading: true,
    isGenerating: false,
    isSaving: false,
  });

  // Fetch existing bio from database
  const fetchBio = useCallback(async () => {
    try {
      const { data: bioData, error } = await supabase
        .from('talent_bios')
        .select('*')
        .eq('talent_type', talentType)
        .eq('talent_id', talentId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching bio:', error);
      }

      if (bioData) {
        setData(prev => ({
          ...prev,
          bio: bioData.is_edited ? bioData.custom_bio : bioData.generated_bio,
          isEdited: bioData.is_edited || false,
          generatedBio: bioData.generated_bio,
          isLoading: false,
        }));
      } else {
        setData(prev => ({
          ...prev,
          bio: existingBio || null,
          isLoading: false,
        }));
      }
    } catch (err) {
      console.error('Error fetching bio:', err);
      setData(prev => ({ ...prev, isLoading: false }));
    }
  }, [talentType, talentId, existingBio]);

  useEffect(() => {
    fetchBio();
  }, [fetchBio]);

  // Generate bio using AI
  const generateBio = useCallback(async () => {
    setData(prev => ({ ...prev, isGenerating: true }));

    try {
      const { data: responseData, error } = await supabase.functions.invoke('generate-talent-bio', {
        body: {
          talentType,
          talentId,
          platform,
          name,
          sport,
          position,
          team,
          league,
          stats,
          followerCount,
          videoCount,
        },
      });

      if (error) throw error;

      setData(prev => ({
        ...prev,
        bio: responseData.bio,
        isEdited: responseData.isEdited || false,
        generatedBio: responseData.generatedBio,
        isGenerating: false,
      }));

      toast({
        title: 'Bio generated',
        description: 'AI-generated bio is ready. You can edit it if needed.',
      });

      return responseData.bio;
    } catch (err) {
      console.error('Error generating bio:', err);
      toast({
        title: 'Generation failed',
        description: 'Could not generate bio. Please try again.',
        variant: 'destructive',
      });
      setData(prev => ({ ...prev, isGenerating: false }));
      return null;
    }
  }, [talentType, talentId, platform, name, sport, position, team, league, stats, followerCount, videoCount, toast]);

  // Save custom bio
  const saveBio = useCallback(async (customBio: string) => {
    setData(prev => ({ ...prev, isSaving: true }));

    try {
      const { error } = await supabase
        .from('talent_bios')
        .upsert({
          talent_type: talentType,
          talent_id: talentId,
          platform: platform || null,
          custom_bio: customBio,
          is_edited: true,
          edited_at: new Date().toISOString(),
        }, {
          onConflict: 'talent_type,talent_id,platform'
        });

      if (error) throw error;

      setData(prev => ({
        ...prev,
        bio: customBio,
        isEdited: true,
        isSaving: false,
      }));

      toast({
        title: 'Bio saved',
        description: 'Your custom bio has been saved.',
      });

      return true;
    } catch (err) {
      console.error('Error saving bio:', err);
      toast({
        title: 'Save failed',
        description: 'Could not save bio. Please try again.',
        variant: 'destructive',
      });
      setData(prev => ({ ...prev, isSaving: false }));
      return false;
    }
  }, [talentType, talentId, platform, toast]);

  // Reset to AI-generated bio
  const resetToGenerated = useCallback(async () => {
    if (!data.generatedBio) {
      toast({
        title: 'No generated bio',
        description: 'Generate a bio first before resetting.',
        variant: 'destructive',
      });
      return false;
    }

    setData(prev => ({ ...prev, isSaving: true }));

    try {
      const { error } = await supabase
        .from('talent_bios')
        .update({
          is_edited: false,
          custom_bio: null,
          edited_at: null,
        })
        .eq('talent_type', talentType)
        .eq('talent_id', talentId);

      if (error) throw error;

      setData(prev => ({
        ...prev,
        bio: prev.generatedBio,
        isEdited: false,
        isSaving: false,
      }));

      toast({
        title: 'Bio reset',
        description: 'Bio has been reset to the AI-generated version.',
      });

      return true;
    } catch (err) {
      console.error('Error resetting bio:', err);
      toast({
        title: 'Reset failed',
        description: 'Could not reset bio. Please try again.',
        variant: 'destructive',
      });
      setData(prev => ({ ...prev, isSaving: false }));
      return false;
    }
  }, [talentType, talentId, data.generatedBio, toast]);

  return {
    ...data,
    generateBio,
    saveBio,
    resetToGenerated,
    refetch: fetchBio,
  };
};
